import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  LinearProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Upload as UploadIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { employeeAPI } from '../services/api';
import { Employee } from '../types/Employee';

interface ImportResult {
  total_rows: number;
  success_count: number;
  error_count: number;
  created_count: number;
  skipped_count: number;
  errors: ImportError[];
  created_employees: Employee[];
  skipped_employees: Employee[];
}

interface ImportError {
  row: number;
  codice_fiscale?: string;
  errors: string[];
}

interface ExcelImportProps {
  onSuccess?: () => void;
  onClose?: () => void;
}

const ExcelImport: React.FC<ExcelImportProps> = ({ onSuccess, onClose }) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setResult(null);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      const data = await employeeAPI.importFromExcel(file, (progress) => {
        setUploadProgress(Math.min(progress, 95)); // Non superare 95% finché non finisce
      });
      setResult(data.result);
      setUploadProgress(100);
      try {
        localStorage.setItem('lastExcelImportResult', JSON.stringify(data.result));
      } catch {}
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
      setUploadProgress(0);
    } finally {
      setUploading(false);
      // Mantieni il 100% per un secondo prima di nascondere
      setTimeout(() => setUploadProgress(0), 1000);
    }
  };

  const handleReset = () => {
    setFile(null);
    setResult(null);
    setError(null);
    setUploading(false);
    setUploadProgress(0);
  };

  const downloadCSV = (filename: string, rows: string[][]) => {
    const csv = rows.map(r => r.map(v => '"' + (v ?? '').replace(/"/g, '""') + '"').join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadErrors = () => {
    if (!result) return;
    const rows: string[][] = [["row", "codice_fiscale", "errors"]];
    result.errors.forEach(e => rows.push([
      String(e.row),
      e.codice_fiscale || '',
      e.errors.join(' | '),
    ]));
    downloadCSV('import-errori.csv', rows);
  };

  const handleDownloadCreated = () => {
    if (!result) return;
    const rows: string[][] = [["nome", "cognome", "matricola", "codice_fiscale", "username", "password_iniziale"]];
    result.created_employees.forEach(e => rows.push([
      e.nome,
      e.cognome,
      e.matricola,
      e.codice_fiscale,
      e.username || '',
      (e.codice_fiscale?.substring(0,6) || '') + '123',
    ]));
    downloadCSV('import-creati.csv', rows);
  };

  const handleDownloadImportReport = async () => {
    if (!result) return;
    try {
      await employeeAPI.downloadImportReport(result);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Errore durante il download del report');
    }
  };


  return (
    <Box>
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="h5" component="h2">
              Import Dipendenti da Excel
            </Typography>
            {result && (
              <Box display="flex" gap={1} flexWrap="wrap">
                <Button variant="contained" size="small" onClick={handleDownloadImportReport} disabled={result.created_employees.length === 0}>
                  Scarica Report Completo
                </Button>
                <Button variant="outlined" size="small" onClick={handleDownloadErrors} disabled={result.errors.length === 0}>
                  Scarica Report Errori
                </Button>
                <Button variant="outlined" size="small" onClick={handleDownloadCreated} disabled={result.created_employees.length === 0}>
                  Scarica Creati
                </Button>
              </Box>
            )}
          </Box>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Carica un file Excel con i dati dei dipendenti. Il sistema:
            <ul style={{ marginTop: '8px', marginBottom: '8px' }}>
              <li>Creerà automaticamente gli account di login per i nuovi dipendenti</li>
              <li>Salterà i dipendenti già presenti nel sistema (identificati tramite codice fiscale)</li>
            </ul>
            <strong>Formati supportati:</strong> Excel con colonne "Nome" e "Cognome" separate 
            oppure "Cognome e Nome" combinato (backward compatibility).
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!result && (
            <Box>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                style={{ 
                  width: '100%', 
                  padding: '12px',
                  border: '2px dashed #ccc',
                  borderRadius: '4px',
                  marginBottom: '16px'
                }}
              />
              
              {file && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    File selezionato: {file.name}
                  </Typography>
                </Box>
              )}

              <Button
                variant="contained"
                startIcon={<UploadIcon />}
                onClick={handleUpload}
                disabled={!file || uploading}
                fullWidth
              >
                {uploading ? 'Importazione in corso...' : 'Importa Dipendenti'}
              </Button>

              {uploading && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress variant="determinate" value={uploadProgress} />
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                    {uploadProgress}%
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {result && (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                Import completato! {result.success_count} operazioni completate con successo.
                {(result.created_count ?? result.created_employees.length) > 0 && ` ${result.created_count ?? result.created_employees.length} creati.`}
                {(result.skipped_count ?? result.skipped_employees?.length ?? 0) > 0 && ` ${result.skipped_count ?? result.skipped_employees?.length ?? 0} saltati (già presenti).`}
              </Alert>

              <Box display="flex" gap={2} mb={3} flexWrap="wrap">
                <Chip
                  icon={<CheckIcon />}
                  label={`${result.success_count} Successi`}
                  color="success"
                />
                {(result.created_count ?? result.created_employees.length) > 0 && (
                  <Chip
                    label={`${result.created_count ?? result.created_employees.length} Creati`}
                    color="primary"
                  />
                )}
                {(result.skipped_count ?? result.skipped_employees?.length ?? 0) > 0 && (
                  <Chip
                    label={`${result.skipped_count ?? result.skipped_employees?.length ?? 0} Saltati`}
                    color="default"
                  />
                )}
                <Chip
                  icon={<ErrorIcon />}
                  label={`${result.error_count} Errori`}
                  color={result.error_count > 0 ? "error" : "default"}
                />
                <Chip
                  label={`${result.total_rows} Righe Totali`}
                  color="info"
                />
              </Box>

              {result.created_employees.length > 0 && (
                <Accordion sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">
                      Dipendenti Creati ({result.created_employees.length}) - Credenziali di Accesso
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        <strong>Credenziali di Accesso:</strong><br/>
                        Username: cognome.nome (es. caliri.claudia)<br/>
                        Password: Prime 6 caratteri del codice fiscale + "123" (es. CLRCLD123)
                      </Typography>
                    </Alert>
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Nome</TableCell>
                            <TableCell>Matricola</TableCell>
                            <TableCell>Codice Fiscale</TableCell>
                            <TableCell>Username</TableCell>
                            <TableCell>Password</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {result.created_employees.map((employee) => (
                            <TableRow key={employee.id}>
                              <TableCell>{employee.nome} {employee.cognome}</TableCell>
                              <TableCell>{employee.matricola}</TableCell>
                              <TableCell>{employee.codice_fiscale}</TableCell>
                              <TableCell>
                                <Chip 
                                  label={employee.username || 'N/A'} 
                                  color="primary" 
                                  size="small" 
                                />
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={`${employee.codice_fiscale.substring(0, 6)}123`} 
                                  color="secondary" 
                                  size="small" 
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              )}

              {result.skipped_employees && result.skipped_employees.length > 0 && (
                <Accordion sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6" color="text.secondary">
                      Dipendenti Saltati ({result.skipped_employees.length}) - Nessuna Modifica
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        I seguenti dipendenti erano già presenti nel sistema e sono stati saltati durante l'import.
                      </Typography>
                    </Alert>
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Nome</TableCell>
                            <TableCell>Cognome</TableCell>
                            <TableCell>Codice Fiscale</TableCell>
                            <TableCell>Matricola</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {result.skipped_employees.map((employee) => (
                            <TableRow key={employee.id}>
                              <TableCell>{employee.nome}</TableCell>
                              <TableCell>{employee.cognome}</TableCell>
                              <TableCell>{employee.codice_fiscale}</TableCell>
                              <TableCell>{employee.matricola}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              )}

              {result.errors.length > 0 && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6" color="error">
                      Errori ({result.errors.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box>
                      {result.errors.map((error, index) => (
                        <Alert key={index} severity="error" sx={{ mb: 1 }}>
                          <Typography variant="body2" fontWeight="bold">
                            Riga {error.row}{error.codice_fiscale ? ` - CF ${error.codice_fiscale}` : ''}:
                          </Typography>
                          <Typography variant="body2">
                            {error.errors.join(', ')}
                          </Typography>
                        </Alert>
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}

              <Box display="flex" gap={2} mt={3}>
                <Button variant="contained" onClick={handleReset}>
                  Nuovo Import
                </Button>
                <Button variant="outlined" onClick={() => window.location.reload()}>
                  Aggiorna Lista
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ExcelImport;
